import React, { useCallback, useId, useRef, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useImageProcessor from '../hooks/useImageProcessor';

const InputFile = ({ onFileReady, onProcessingChange, label, inputId }) => {
  const { t } = useTranslation('description', {
    keyPrefix: 'orders.deliver.input_file',
  });
  const generatedId = useId();
  const id = inputId ?? generatedId;
  const ref = useRef(null);
  const [displayName, setDisplayName] = useState('');

  const { processImage, isProcessing } = useImageProcessor();

  const handleFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setDisplayName(file.name);
      onProcessingChange?.(true);

      const result = await processImage(file);

      if (!result) {
        ref.current.value = null;
        setDisplayName('');
        onFileReady(null);
      } else {
        onFileReady(result);
      }

      onProcessingChange?.(false);
    },
    [processImage, onFileReady, onProcessingChange]
  );

  const noFilesLabel = t('orders.deliver.input_file.no_files_selected', {
    ns: 'description',
  });
  const selectLabel = t('orders.deliver.input_file.select_files', {
    ns: 'description',
  });

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {label && (
        <Typography
          component="div"
          variant="body2"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
            textAlign: 'left',
            lineHeight: 1.5,
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 48,
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          pointerEvents: isProcessing ? 'none' : 'auto',
          bgcolor: '#fff',
        }}
      >
        <Button
          component="label"
          disabled={isProcessing}
          sx={{
            borderRadius: 0,
            flexShrink: 0,
            px: 2.5,
            py: 1.25,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            bgcolor: '#EEEEEE',
            color: 'rgba(0, 0, 0, 0.65)',
            boxShadow: 'none',
            borderRight: '1px solid #E0E0E0',
            '&:hover': {
              bgcolor: '#E8E8E8',
              boxShadow: 'none',
            },
          }}
        >
          {selectLabel}
          <input
            ref={ref}
            id={id}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            disabled={isProcessing}
            style={{ display: 'none' }}
          />
        </Button>
        <Box
          sx={{
            flex: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            bgcolor: '#fff',
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: '0.8125rem',
              color: displayName
                ? 'rgba(0, 0, 0, 0.75)'
                : 'rgba(0, 0, 0, 0.38)',
            }}
          >
            {displayName || noFilesLabel}
          </Typography>
        </Box>
        {isProcessing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={28} color="primary" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InputFile;
